import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
} from "@app/constants";
import CampaignService from "@app/services/http/campaign.service";
import { CampaignPopupBanner, Meta, TypeToggleStatus } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "@app/utils/util";
import { useNavigate } from "react-router-dom";
import Checkbox from "@app/components/checkbox";
import useForceUpdate from "@core/hooks/use-force-update.hook";

function MediaUrlColumn({ data }: { data: CampaignPopupBanner }) {
  return (
    <div className="truncate max-w-[320px] text-blue-500 hover:underline">
      {data.media ? (
        <a href={data.media} target="_blank" rel="noreferrer">
          {data.media}
        </a>
      ) : (
        "-"
      )}
    </div>
  );
}

function UserTypePill({ data }: { data: CampaignPopupBanner }) {
  const label = useMemo(() => {
    if (data.userType === 2) return "VIP";

    if (data.userType === 1) return "Miễn phí";

    return "All";
  }, [data.userType]);

  const className = useMemo(() => {
    if (label === "VIP") return "bg-[#FFF0E6] text-[#FE7E20]";

    if (label === "Miễn phí") return "bg-[#E6FFED] text-[#00A653]";

    return "bg-[#FFEBEB] text-[#FC4040]";
  }, [label]);

  return (
    <span
      className={`px-3 py-1 rounded-lg text-xs whitespace-nowrap ${className}`}
    >
      {label}
    </span>
  );
}

function StartDateColumn({ data }: { data: CampaignPopupBanner }) {
  return <div>{data.startDate ? formatDate(data.startDate) : "-"}</div>;
}

function EndDateColumn({ data }: { data: CampaignPopupBanner }) {
  return <div>{data.endDate ? formatDate(data.endDate) : "-"}</div>;
}

function DisplayCountColumn({ data }: { data: CampaignPopupBanner }) {
  return <div className="text-center">{data.totalDisplayCount ?? 0}</div>;
}

function DisplayTimeColumn({ data }: { data: CampaignPopupBanner }) {
  const seconds = data.totalDisplayTime ?? 0;

  return <div className="text-center">{`${seconds}s`}</div>;
}

function TestingIdsColumn({ data }: { data: CampaignPopupBanner }) {
  const value = Array.isArray(data.testingIds)
    ? data.testingIds.join(", ")
    : data.testingIds || "-";

  return (
    <div className="truncate min-w-[120px] max-w-[240px]">{String(value)}</div>
  );
}

// will define columnConfig inside BannerTable to access ActiveCheckbox

function BannerTable() {
  const columnConfig: ColumnConfig[] = [
    {
      label: "ID",
      dataKey: "id",
      width: "8%",
      tdClassName: "truncate min-w-[70px] max-w-[120px]",
      hasOrder: true,
    },
    {
      label: "Nội dung",
      dataKey: "content",
      width: "24%",
      tdClassName: "truncate min-w-[200px] max-w-[360px]",
      hasOrder: true,
    },
    {
      label: "Loại user",
      dataKey: "userType",
      width: "12%",
      component: UserTypePill,
      hasOrder: true,
    },
    {
      label: "Media url",
      dataKey: "media",
      width: "22%",
      component: MediaUrlColumn,
      hasOrder: true,
    },
    {
      label: "Start date",
      dataKey: "startDate",
      width: "12%",
      component: StartDateColumn,
      hasOrder: true,
    },
    {
      label: "End date",
      dataKey: "endDate",
      width: "12%",
      component: EndDateColumn,
      hasOrder: true,
    },
    {
      label: "TĐB",
      dataKey: "totalDisplayCount",
      width: "5%",
      component: DisplayCountColumn,
      hasOrder: true,
    },
    {
      label: "TĐB",
      dataKey: "totalDisplayTime",
      width: "5%",
      component: DisplayTimeColumn,
      hasOrder: true,
    },
    {
      label: "Trạng thái",
      dataKey: "isActive",
      width: "10%",
      component: ({ data }: { data: CampaignPopupBanner }) => (
        <ActiveCheckbox data={data} />
      ),
      hasOrder: true,
    },
    {
      label: "Id test",
      dataKey: "testingIds",
      width: "20%",
      component: TestingIdsColumn,
      hasOrder: false,
    },
  ];
  const [banners, setBanners] = useState<CampaignPopupBanner[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const { abortController } = useCancellationRequest();
  const [update, forceUpdate] = useForceUpdate();
  const navigate = useNavigate();

  useEffect(() => {
    CampaignService.getPopupBannerList(
      requestParams,
      abortController.signal
    ).then((res) => {
      setBanners(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [requestParams, update]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const ActiveCheckbox = ({ data }: { data: CampaignPopupBanner }) => {
    const handleToggle = (e: any) => {
      e.stopPropagation();

      CampaignService.toggleStatus(data.id, TypeToggleStatus.POPUP_BANNER).then(
        () => {
          forceUpdate();
        }
      );
    };

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={Boolean(data.isActive)}
          onChange={handleToggle}
          checkboxClassName="w-4 h-4 cursor-pointer"
        />
      </div>
    );
  };

  const handleEditClick = (banner: CampaignPopupBanner) => {
    navigate(
      `/${RoutePath.CAMPAIGN_MANAGEMENT}/${RoutePath.UPDATE_CAMPAIGN}/${banner.id}?type=banners`
    );
  };

  return (
    <div className="mt-6 rounded-xl bg-white py-6 px-5">
      <TitleDescription
        title="Danh sách popup banner"
        onInputChange={onSearchChange}
      />
      <div className="mt-6">
        <Table
          columnConfig={columnConfig}
          dataTable={banners}
          hasCheckBox
          hasAction
          onEditClick={handleEditClick}
          meta={meta}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onRowClickOrder={onOrderChange}
          componentEmpty={
            <div className="text-center py-3 bg-[#f4f4f4]">
              <p className="text-[#666] text-xl">Không có dữ liệu</p>
            </div>
          }
        />
      </div>
    </div>
  );
}

export default BannerTable;
